import React, { useMemo, useState } from 'react';
import { Pencil, Target } from 'lucide-react';
import { fmtKwh, fmtPct } from '../../../../src/lib/funnelUtils';
import { linhasAprovadasNoFechamento } from '../../../../src/lib/fechamentoMensal';
import { computeMetaRealizado, RealRow } from '../../../../src/lib/metaRealizadoCalc';
import { NegRow } from '../types';
import EditarMetaDoTimeModal from './EditarMetaDoTimeModal';

interface MetaRealizadoBlockProps {
    /** Linhas já filtradas por responsável/canal/cidade/origem/status — NÃO por período,
     * já que este bloco resolve seu próprio mês via `mesUnico`. */
    rows: NegRow[];
    mesUnico: { mes: number; ano: number } | null;
    mesLabel: string;
    /** Meta já resolvida pelo pai (ver src/lib/metasComerciais.ts): sem filtro, a do time
     * (`metas_mensais`); com filtro de canal ou pessoa, a soma das metas da Folha do recorte. */
    meta: number;
    /** De onde veio a meta, em texto curto ("definida para o mês", "soma das metas da Folha"...). */
    origemMeta: string | null;
    /** true = o recorte está filtrado por canal ou pessoa. */
    recorte: boolean;
    /** Meta do time no mês, para abrir o editor com o valor atual (mesmo com recorte). */
    metaDoTime: number | null;
    /** Só adm grava a meta do time — a mesma regra da aba Metas do Painel Executivo. */
    podeEditar: boolean;
    onSalvarMetaDoTime: (valor: number, observacao: string) => Promise<void>;
}

const statusTone = (pct: number) => pct >= 100 ? 'text-green-600 dark:text-green-400' : pct >= 90 ? 'text-amber-500' : 'text-red-500';

const EditButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <button
        onClick={onClick}
        title="Editar a meta do Comercial (a mesma do Painel Executivo)"
        className="p-1 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
    >
        <Pencil size={13} />
    </button>
);

/** Meta x Realizado fundido no Analytics — só gerente/adm, só quando o período selecionado
 * resolve a exatamente um mês-calendário (precisa de mês/ano fechado pra calcular
 * "realizado"). "Aprovados" usa a regra de fechamento (carência de 3 dias) desde o início.
 *
 * A meta vem do banco, uma fonte por nível (src/lib/metasComerciais.ts): sem filtro, a meta
 * do time em `metas_mensais` — a mesma da aba Metas do Painel Executivo; com filtro de canal
 * ou pessoa, a soma das metas individuais da Folha de Pagamento. O lápis edita a meta do
 * time, e a mudança vale também no Painel Executivo e na Visão Geral. */
const MetaRealizadoBlock: React.FC<MetaRealizadoBlockProps> = ({
    rows, mesUnico, mesLabel, meta, origemMeta, recorte, metaDoTime, podeEditar, onSalvarMetaDoTime,
}) => {
    const [editOpen, setEditOpen] = useState(false);

    const viewRows: RealRow[] = useMemo(() => {
        if (!mesUnico) return [];
        return linhasAprovadasNoFechamento(rows, mesUnico.mes, mesUnico.ano);
    }, [rows, mesUnico]);

    const computed = useMemo(() => {
        if (!mesUnico) return null;
        const dateStart = `${mesUnico.ano}-${String(mesUnico.mes).padStart(2, '0')}-01`;
        const dateEnd = new Date(mesUnico.ano, mesUnico.mes, 0).toISOString().split('T')[0];
        return computeMetaRealizado({ viewRows, totalMeta: meta, dateStart, dateEnd });
    }, [viewRows, meta, mesUnico]);

    const editor = editOpen && mesUnico && (
        <EditarMetaDoTimeModal
            mesLabel={mesLabel}
            valorAtual={metaDoTime}
            onSave={async (valor, observacao) => { await onSalvarMetaDoTime(valor, observacao); setEditOpen(false); }}
            onClose={() => setEditOpen(false)}
        />
    );

    if (!mesUnico || !computed) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-1">
                    <Target size={15} className="text-primary" />
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white">Meta x Realizado</h2>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Meta não aplicável a este período — o cálculo de realizado precisa de um mês-calendário exato. Selecione "Mês atual", "Mês anterior" ou um período personalizado que cubra exatamente um mês.
                </p>
            </div>
        );
    }

    const semMeta = meta <= 0;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-1">
                <Target size={15} className="text-primary" />
                <h2 className="text-sm font-bold text-gray-900 dark:text-white">Meta x Realizado — {mesLabel}</h2>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/90 text-white">OFICIAL</span>
                {podeEditar && <EditButton onClick={() => setEditOpen(true)} />}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                "Aprovados" considera a regra de fechamento (carência de 3 dias do mês seguinte) — mesmo critério do Performance Comercial.
            </p>
            <p className="text-[11px] font-semibold text-primary mb-4">
                {recorte
                    ? `Meta do recorte: soma das metas da Folha de Pagamento de quem está no filtro${origemMeta ? ` (${origemMeta})` : ''}.`
                    : semMeta
                        ? `Sem meta do Comercial para ${mesLabel}. ${podeEditar ? 'Defina no lápis acima' : 'Um administrador define'} ou na aba Metas do Painel Executivo — é a mesma meta.`
                        : `Meta do Comercial, todos os canais${origemMeta ? ` — ${origemMeta}` : ''}. É a mesma da aba Metas do Painel Executivo.`}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
                    <p className="text-[10px] font-bold text-gray-400 uppercase">Meta</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{semMeta ? '—' : fmtKwh(meta)}</p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
                    <p className="text-[10px] font-bold text-gray-400 uppercase">Realizado</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{fmtKwh(computed.totalVolume)}</p>
                    <p className={`text-[11px] font-bold ${statusTone(computed.metaPct)}`}>{!semMeta ? `${fmtPct(computed.metaPct)} da meta` : ''}</p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
                    <p className="text-[10px] font-bold text-gray-400 uppercase">Gap p/ meta</p>
                    <p className="text-lg font-bold text-red-500">{semMeta ? '—' : fmtKwh(computed.gap)}</p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
                    <p className="text-[10px] font-bold text-gray-400 uppercase">Projeção de fechamento</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{computed.bizPassed > 0 ? fmtKwh(computed.projecao) : '—'}</p>
                    <p className={`text-[11px] font-bold ${!semMeta ? statusTone(computed.projecaoPct) : ''}`}>{computed.bizPassed > 0 && !semMeta ? `${fmtPct(computed.projecaoPct)} projetado` : ''}</p>
                </div>
            </div>

            {editor}
        </div>
    );
};

export default MetaRealizadoBlock;
